<template>
  <form
    class="donor-form-card"
    method="POST"
    target="_blank"
    @submit.prevent="submitContactDataForm"
  >
    <div class="donor-form-card__body">
      <div class="donor-form-header">
        <div class="donor-form-header__icon">
          <MapPinIcon class="h-5 w-5 text-primaryAccent" aria-hidden="true" />
        </div>
        <div class="donor-form-header__text">
          <h2 class="donor-form-title">Contact Details</h2>
          <p class="donor-form-subtitle">How can we reach you about your donation?</p>
        </div>
      </div>

      <section class="donor-form-section">
        <p class="donor-form-section-title">Address</p>
        <div class="donor-form-address">
          <div class="donor-form-grid-thirds">
            <div class="donor-form-field-two-thirds">
              <PlacesStreetAddressInput
                label-size="small"
                input-text-size="small"
                v-model:value="form.addressStreet.value"
                :errors="form.addressStreet.errors"
                label="Street address"
                type="text"
                id="street-address"
                placeholder="Start typing your address..."
                @place-selected="onAddressPlaceSelected"
                @invalid-selection="onAddressInvalidSelection"
                @blur="handleAddressBlur"
                @input="handleAddressInput"
              />
            </div>
            <InputField
              label-size="small"
              input-text-size="small"
              v-model:value="form.addressDetail.value"
              :errors="form.addressDetail.errors"
              @blur="form.addressDetail.validate()"
              label="Apt, Floor, or Suite"
              type="text"
              text="address-detail"
              id="address-detail"
              placeholder="Optional"
            />
          </div>
          <div class="donor-form-grid-thirds">
            <InputField
              label-size="small"
              input-text-size="small"
              v-model:value="form.city.value"
              :errors="form.city.errors"
              @blur="form.city.validate()"
              label="City"
              type="text"
              text="city"
              id="city"
              placeholder="City"
              autocomplete="address-level2"
              :readonly="addressAutofilled"
            />
            <SelectField
              small
              :select-options="usStateData"
              v-model:value="form.state.value"
              label="State"
              :errors="form.state.errors"
              :disabled="addressAutofilled"
            />
            <InputField
              label-size="small"
              input-text-size="small"
              v-model:value="form.postalCode.value"
              :errors="form.postalCode.errors"
              @blur="form.postalCode.validate()"
              label="Zip / Postal code"
              type="text"
              text="postal-code"
              id="postal-code"
              placeholder="ZIP"
              autocomplete="postal-code"
              :readonly="addressAutofilled"
            />
          </div>
        </div>
      </section>

      <section class="donor-form-section donor-form-section-divider">
        <p class="donor-form-section-title">Contact information</p>
        <div class="donor-form-grid-thirds">
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.email.value"
            :errors="form.email.errors"
            @blur="handleEmailBlur"
            label="Email"
            type="email"
            text="email"
            id="email"
            placeholder="example@email.com"
            autocomplete="email"
          />
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.mobilePhone.value"
            :errors="form.mobilePhone.errors"
            @blur="handlePhoneBlur('mobilePhone')"
            @input="formatPhoneNumber('mobilePhone')"
            label="Cell phone"
            type="text"
            text="mobile-phone"
            id="mobile-phone"
            placeholder="XXX-XXX-XXXX"
            autocomplete="phone"
            pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
            title="Phone number should be correct"
          />
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.homePhone.value"
            :errors="form.homePhone.errors"
            @blur="handlePhoneBlur('homePhone')"
            @input="formatPhoneNumber('homePhone')"
            label="Primary phone"
            type="text"
            text="home-phone"
            id="home-phone"
            placeholder="XXX-XXX-XXXX"
            pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
            title="Phone number should be correct"
          />
        </div>
      </section>

      <section
        v-if="paymentProviders[paymentProvider] !== EngagingNetwork"
        class="donor-form-section donor-form-section-divider"
      >
        <p class="donor-form-section-title">Preferred communication method</p>
        <div class="donor-form-grid-thirds">
          <div
            v-for="(option, i) in [
              form.isEmailPreferred,
              form.isSmsPreferred,
              form.isPhoneCallPreferred,
            ]"
            :key="i"
          >
            <input
              :id="option.label"
              type="checkbox"
              class="peer sr-only"
              :value="true"
              v-model="option.value"
            />
            <label :for="option.label" class="donor-form-checkbox-card">
              <span class="text-sm font-medium text-gray-900">{{ option.label }}</span>
            </label>
          </div>
        </div>
      </section>

      <template v-if="paymentProviders[paymentProvider] === EngagingNetwork">
        <section class="donor-form-section donor-form-section-divider">
          <label for="origin-source" class="input--label--small text-gray-900">
            WWF’s work focuses around 6 key areas. Which do you feel most passionate about?
          </label>
          <select
            v-model="form.originSource.value"
            id="origin-source"
            class="input--text--small mt-1 w-full"
          >
            <option value="" disabled selected>Select an option</option>
            <option v-for="option in originSourceOptions" :key="option.id" :value="option.id">
              {{ option.name }}
            </option>
          </select>
          <p v-if="form.originSource.errors.length > 0" class="input--error">
            {{ form.originSource.errors[0].message }}
          </p>
        </section>

        <section class="donor-form-section donor-form-panel">
          <p class="donor-form-section-title">Be a part of our community</p>
          <p class="text-sm text-gray-600">
            We’ll send you updates on the impact of your donation – and how you can protect nature
            through activism, donation, events, and more. You can unsubscribe at any time. By
            providing your mobile number, you agree to receive recurring text messages from WWF.
            Text STOP to quit, HELP for info. Message and data rates may apply.
          </p>

          <div>
            <label class="input--label--small"
              >I would like to get (or continue to get) email updates</label
            >
            <div class="mt-2 flex gap-4">
              <label class="inline-flex items-center gap-2 text-sm text-gray-900">
                <input
                  id="email-updates-yes"
                  name="email-updates"
                  type="radio"
                  value="Y"
                  v-model="form.emailUpdates.value"
                  class="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                />
                Yes
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-gray-900">
                <input
                  id="email-updates-no"
                  name="email-updates"
                  type="radio"
                  value="N"
                  v-model="form.emailUpdates.value"
                  class="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                />
                No
              </label>
            </div>
          </div>

          <div>
            <label class="input--label--small">I would like to get WWF Text messages</label>
            <div class="mt-2 flex gap-4">
              <label class="inline-flex items-center gap-2 text-sm text-gray-900">
                <input
                  id="text-messages-yes"
                  name="text-messages"
                  type="radio"
                  value="Y"
                  v-model="form.textMessages.value"
                  class="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                />
                Yes
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-gray-900">
                <input
                  id="text-messages-no"
                  name="text-messages"
                  type="radio"
                  value="N"
                  v-model="form.textMessages.value"
                  class="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                />
                No
              </label>
            </div>
          </div>
        </section>
      </template>
    </div>

    <div class="donor-form-actions">
      <BaseButton type="button" variant="secondary" @click="prevStep">Back</BaseButton>
      <BaseButton type="submit" icon-position="trailing" :icon="ArrowRightIcon">
        Save &amp; Continue
      </BaseButton>
    </div>
  </form>
</template>

<script>
import { onBeforeMount, ref, watch } from 'vue'
import { ArrowRightIcon, MapPinIcon } from '@heroicons/vue/20/solid'
import SelectField from '@/components/inputs/SelectField'
import InputField from '@/components/inputs/InputField'
import PlacesStreetAddressInput from '@/components/inputs/PlacesStreetAddressInput.vue'
import { ContactDonorForm } from '@/services/users/'
import usStateData from '@/data/usStateData.js'
import { CampaignApi } from '@/services/campaign/'
import { resolveFormToken } from '@/services/formAccess'
import { useRoute, useRouter } from 'vue-router'
import { BBMSSkyPayments, EngagingNetwork, paymentProviders } from '@/services/npo'
import {
  validateEmail,
  getValidationErrorMessage,
  shouldRejectEmail,
} from '@/services/emailVerification'
import { UserApi } from '@/services/users/'

export default {
  name: 'DonorFormContact',
  components: {
    SelectField,
    InputField,
    PlacesStreetAddressInput,
    MapPinIcon,
    ArrowRightIcon,
  },
  props: {
    backStep: [Number, String],
    initialValues: {
      type: Object,
      default: null,
    },
  },
  emits: ['contactData', 'stepNumber'],
  setup(props, { emit }) {
    const form = ref(new ContactDonorForm())

    const applyInitialValues = (values) => {
      if (!values || !Object.keys(values).length) return
      form.value = ContactDonorForm.create({ ...values })
    }

    watch(() => props.initialValues, applyInitialValues, { immediate: true, deep: true })

    const route = useRoute()
    const router = useRouter()
    const campaignId = route.params.id
    const formToken = resolveFormToken(route)
    const campaign = ref()
    const paymentProvider = ref()
    const addressAutofilled = ref(false)

    // Email validation state
    const emailValidating = ref(false)
    const emailValidationMessage = ref('')
    let emailValidationTimeout = null
    let lastValidatedEmail = null
    let lastValidationResult = null // Cache last validation result
    let isSubmitting = false // Flag to prevent blur validation during form submission
    let validationPromise = null // Track ongoing validation promise to prevent duplicate calls

    // Phone screening cache (same idea as email: blur + submit share one API result)
    let phoneValidationTimeouts = { mobilePhone: null, homePhone: null }
    let lastValidatedPhones = { mobilePhone: null, homePhone: null }
    let lastPhoneResults = { mobilePhone: null, homePhone: null }
    let phoneValidationPromises = { mobilePhone: null, homePhone: null }

    // function to wait for ongoing validation
    async function waitForValidation() {
      if (!emailValidating.value) return

      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!emailValidating.value) {
            clearInterval(checkInterval)
            resolve()
          }
        }, 100)
      })
    }
    async function validateEmailWithCache(email) {
      // Return cached result if available
      if (email === lastValidatedEmail && lastValidationResult) {
        return lastValidationResult
      }

      // Wait for any ongoing validation
      await waitForValidation()

      // Check cache again after waiting (might have been updated)
      if (email === lastValidatedEmail && lastValidationResult) {
        return lastValidationResult
      }

      // Prevent duplicate API calls - reuse existing promise if same email is being validated
      if (validationPromise && email === lastValidatedEmail) {
        return validationPromise
      }

      // Start new validation
      emailValidating.value = true
      validationPromise = validateEmail(email)
        .then((result) => {
          lastValidatedEmail = email
          lastValidationResult = result
          return result
        })
        .catch((error) => {
          console.error('Email validation error:', error)
          lastValidatedEmail = null
          lastValidationResult = null
          throw error
        })
        .finally(() => {
          emailValidating.value = false
          validationPromise = null
        })

      return validationPromise
    }

    // function to apply validation result to form
    function applyValidationResult(validationResult, email) {
      if (!validationResult) return

      if (shouldRejectEmail(validationResult)) {
        const errorMessage = getValidationErrorMessage(validationResult)
        emailValidationMessage.value = errorMessage
        form.value.email.errors = [{ message: errorMessage }]
      } else {
        emailValidationMessage.value = ''
        if (validationResult.api_available) {
          form.value.email.errors = []
        }
      }
    }

    // Common function to scroll to email field and focus
    function focusEmailField() {
      const emailInput = document.getElementById('email')
      if (emailInput) {
        emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
        emailInput.focus()
      }
    }

    function applyPhoneScreeningResult(fieldName, allowed) {
      if (!allowed) {
        form.value[fieldName].errors = [
          {
            message: 'Please enter a valid phone number.',
            code: 'screened_phone',
          },
        ]
      }
    }

    async function checkPhoneScreening(fieldName) {
      const phone = form.value[fieldName].value?.trim()
      if (!phone) {
        lastValidatedPhones[fieldName] = null
        lastPhoneResults[fieldName] = null
        return true
      }

      // Return cached result if same phone was already checked
      if (phone === lastValidatedPhones[fieldName] && lastPhoneResults[fieldName] !== null) {
        applyPhoneScreeningResult(fieldName, lastPhoneResults[fieldName])
        return lastPhoneResults[fieldName]
      }

      // Wait for any in-flight request, then reuse cache if phone still matches
      if (phoneValidationPromises[fieldName]) {
        await phoneValidationPromises[fieldName]
        if (phone === lastValidatedPhones[fieldName] && lastPhoneResults[fieldName] !== null) {
          applyPhoneScreeningResult(fieldName, lastPhoneResults[fieldName])
          return lastPhoneResults[fieldName]
        }
      }

      phoneValidationPromises[fieldName] = UserApi.csc
        .checkScreenedPhone({ phone })
        .then((result) => {
          const allowed = result.allowed !== false
          lastValidatedPhones[fieldName] = phone
          lastPhoneResults[fieldName] = allowed
          applyPhoneScreeningResult(fieldName, allowed)
          return allowed
        })
        .catch((error) => {
          console.error('Phone screening error:', error)
          lastValidatedPhones[fieldName] = null
          lastPhoneResults[fieldName] = null
          return true
        })
        .finally(() => {
          phoneValidationPromises[fieldName] = null
        })

      return phoneValidationPromises[fieldName]
    }

    async function handlePhoneBlur(fieldName) {
      if (isSubmitting) {
        return
      }

      form.value[fieldName].validate()
      const phone = form.value[fieldName].value?.trim()

      if (!phone || form.value[fieldName].errors.length > 0) {
        lastValidatedPhones[fieldName] = null
        lastPhoneResults[fieldName] = null
        return
      }

      if (phoneValidationTimeouts[fieldName]) {
        clearTimeout(phoneValidationTimeouts[fieldName])
      }

      phoneValidationTimeouts[fieldName] = setTimeout(async () => {
        if (isSubmitting) {
          return
        }

        const currentPhone = form.value[fieldName].value?.trim()
        if (currentPhone !== phone || !currentPhone) {
          return
        }

        await checkPhoneScreening(fieldName)
      }, 300)
    }

    onBeforeMount(async () => {
      campaign.value = await CampaignApi.csc.customRetrieve({
        campaign: campaignId,
        formToken,
      })
      paymentProvider.value = campaign.value.latestPaymentProvider?.paymentProvider

      if (paymentProvider.value !== 4) {
        form.value.originSource.value = originSourceOptions.value[0].id
      }

      // Operation Smile asked for email, text and phone to start selected. Values restored
      // from an earlier step or a resubmit keep whatever the donor chose.
      const hasRestoredValues = props.initialValues && Object.keys(props.initialValues).length
      if (paymentProviders[paymentProvider.value] === BBMSSkyPayments && !hasRestoredValues) {
        form.value.isEmailPreferred.value = true
        form.value.isSmsPreferred.value = true
        form.value.isPhoneCallPreferred.value = true
      }
    })

    function onAddressPlaceSelected(parsed) {
      form.value.city.value = parsed.city
      form.value.state.value = parsed.state
      form.value.postalCode.value = parsed.postalCode
      addressAutofilled.value = true
      form.value.addressStreet.errors = []
    }

    function onAddressInvalidSelection() {
      form.value.addressStreet.errors = [
        { message: 'Please select a valid address from the dropdown' },
      ]
    }

    function handleAddressBlur() {
      form.value.addressStreet.validate()
      if (!addressAutofilled.value && form.value.addressStreet.value) {
        form.value.addressStreet.value = ''
      }
    }

    function handleAddressInput() {
      // If user starts typing manually, reset the autofilled flag
      if (addressAutofilled.value) {
        addressAutofilled.value = false
        form.value.addressStreet.value = ''
        form.value.city.value = ''
        form.value.state.value = ''
        form.value.postalCode.value = ''
      }
    }

    const originSourceOptions = ref([
      { id: 'Climate', name: 'Climate' },
      { id: 'Oceans', name: 'Oceans' },
      { id: 'Freshwater', name: 'Freshwater' },
      { id: 'Food', name: 'Sustainable Agriculture' },
      { id: 'Forests', name: 'Forests' },
      { id: 'Species', name: 'Wildlife' },
    ])

    function formatPhoneNumber(fieldName) {
      const phone = form.value[fieldName].value.replace(/\D/g, '')
      let formatted = phone

      if (phone.length >= 4) {
        formatted = phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
      }

      form.value[fieldName].value = formatted
    }

    async function handleEmailBlur() {
      // Don't validate on blur if form is being submitted
      if (isSubmitting) {
        return
      }

      // Basic format validation
      form.value.email.validate()
      const email = form.value.email.value?.trim()

      if (!email || form.value.email.errors.length > 0) {
        emailValidationMessage.value = ''
        lastValidatedEmail = null
        lastValidationResult = null
        return
      }

      // Clear previous timeout
      if (emailValidationTimeout) {
        clearTimeout(emailValidationTimeout)
      }

      // Debounce validation - wait 300ms after user stops typing
      emailValidationTimeout = setTimeout(async () => {
        // Check if submission started or email changed during debounce
        if (isSubmitting) {
          return
        }

        const currentEmail = form.value.email.value?.trim()
        if (currentEmail !== email || !currentEmail) {
          return
        }

        try {
          const validationResult = await validateEmailWithCache(email)

          // Final check if email still matches
          if (form.value.email.value?.trim() !== email) {
            return
          }

          applyValidationResult(validationResult, email)
        } catch (error) {
          // Error during validation - don't block form submission
          // Error already logged in validateEmailWithCache
        }
      }, 300)
    }

    async function submitContactDataForm() {
      // Set flag to prevent blur validation from running during submission
      isSubmitting = true

      const unwrappedForm = form.value

      // Trim all string values in the form first
      Object.keys(unwrappedForm).forEach((key) => {
        if (unwrappedForm[key]?.value && typeof unwrappedForm[key].value === 'string') {
          unwrappedForm[key].value = unwrappedForm[key].value.trim().replace(/\s+/g, ' ')
        }
      })

      // Clear any pending email validation timeout
      if (emailValidationTimeout) {
        clearTimeout(emailValidationTimeout)
        emailValidationTimeout = null
      }

      // Clear any pending phone screening timeouts
      ;['mobilePhone', 'homePhone'].forEach((fieldName) => {
        if (phoneValidationTimeouts[fieldName]) {
          clearTimeout(phoneValidationTimeouts[fieldName])
          phoneValidationTimeouts[fieldName] = null
        }
      })

      // Validate email with API before submission
      const email = unwrappedForm.email.value?.trim()
      if (email) {
        try {
          const validationResult = await validateEmailWithCache(email)

          // Block submission if email should be rejected
          if (validationResult && shouldRejectEmail(validationResult)) {
            isSubmitting = false
            applyValidationResult(validationResult, email)
            focusEmailField()
            return
          }
        } catch (error) {
          // Allow submission if validation fails (backend will validate)
          // Error already logged in validateEmailWithCache
        }
      }

      // Validate all other fields
      unwrappedForm.validate()

      const mobileAllowed = await checkPhoneScreening('mobilePhone')
      const homeAllowed = await checkPhoneScreening('homePhone')
      if (!mobileAllowed || !homeAllowed) {
        isSubmitting = false
        return
      }

      // Final check - block if email has errors
      if (unwrappedForm.email.errors.length > 0) {
        isSubmitting = false
        focusEmailField()
        return
      }

      if (!unwrappedForm.isValid) {
        isSubmitting = false
        return
      }

      // Reset submission flag before emitting events
      isSubmitting = false

      emit('contactData', unwrappedForm.value)
      emit('stepNumber', 2)
    }

    function prevStep() {
      emit('stepNumber', props.backStep ?? 0)
    }

    return {
      form,
      submitContactDataForm,
      usStateData,
      prevStep,
      formatPhoneNumber,
      originSourceOptions,
      EngagingNetwork,
      paymentProviders,
      paymentProvider,
      handleAddressInput,
      addressAutofilled,
      handleAddressBlur,
      onAddressPlaceSelected,
      onAddressInvalidSelection,
      handleEmailBlur,
      handlePhoneBlur,
      emailValidating,
      emailValidationMessage,
      ArrowRightIcon,
    }
  },
}
</script>
